import { useUpdatedNotificationListener } from '@cscglobal-tbs/notifications-data-tanstack';
import {
    ExportFileNotificationActions,
    IAlertNotification,
    ICollaborationNotification,
    IExportFileNotification,
    NotificationBell,
    NotificationType,
    useNotificationLinks,
    useNotificationsErrorStatus,
} from '@cscglobal-tbs/notifications-ui';
import { CommonTopBar, CorptaxHomeLogoLink } from '@cscglobal-tbs/react-components-core';
import { makeStyles, Text } from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../api/features/features';
import { useGetNotifications, useUpdateNotification } from '../../api/notifications/notifications';
import { useGetClaimsForCurrentUser } from '../../api/user/user';
import { useGetClaims } from '../../apiExtensions/account/account';
import { useNotification } from '../../contexts/useNotification';
import { useExportReports } from '../../hooks/useExportReports';
import { useIndexedDB } from '../../hooks/useIndexedDB';
import { useNewNotificationListenerHelper } from '../../hooks/useNewNotificationListenerHelper';
import { useNotificationSettingsProps } from '../../hooks/useNotificationSettingsProps';
import { useServiceDiscovery } from '../../hooks/useServiceDiscovery';
import { useTaxReturnCalculationState } from '../../hooks/useTaxReturnCalculationState';
import { PILLAR2_SUBMITTED_REPORT_PREFIX } from '../../utils/BrowserStorageKeys.constants';
import { EnterpriseSelectorEnabled } from '../../utils/FeatureFlags.constants';
import { DB_FORM_CACHE_NAME } from '../../utils/FormIndexedDBUtils';
import { clearFormZoomSettingsFromSession } from '../../utils/FormZoomSettings';
import { DB_VERSION } from '../../utils/IndexedDBSchemaRegistry';
import { replaceLocation } from '../../utils/LocationUtils';
import { clearPillar2UkAuthOnBff } from '../../utils/pillar2UkAuthSignOut';
import { BFF_LOGOUT_URL_CLAIM_TYPE, getLogoutUrl, getLogoutUrlWithSignInAsAnotherUser, LOGIN_URL } from '../../utils/Urls.constant';
import { DB_WHITEPAPERREPORT_NAME } from '../../utils/WhitePaperIndexedDBUtils';
import { EnterpriseSelectorContainer } from '../enterprise-selector-container/EnterpriseSelectorContainer';
import { StyledHtmlViewer } from '../html-viewer/StyledHtmlViewer';
import { UserMenuContainer } from './user-menu/UserMenuContainer';

const useStyles = makeStyles({
    titleContainer: {
        height: '100%',
        alignItems: 'center',
        '& h1': {
            fontSize: '1.25rem',
            marginLeft: '.5rem',
        },
    },
});

export const Topbar: FC = () => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { refetch: fetchSessionClaims } = useGetClaims();
    const { data: userClaims } = useGetClaimsForCurrentUser();
    const { homeUrl, calculationReviewUrl, idpUrl } = useServiceDiscovery();
    const { clearAllCompletedReports } = useTaxReturnCalculationState();
    const { signalRConnectionInfo, isSignalRConnectionInformationErrored, refetchSignalRConnectionInformation } = useNotification();
    const { data: isEnterpriseSelectorEnabled, isLoading: isFeatureFlagLoading } = useIsFeatureEnabled({
        featureFlagName: EnterpriseSelectorEnabled,
    });
    const { downloadTaxReturnPdf, printTaxReturnPdf } = useExportReports();

    const portalUrl = userClaims && `${userClaims?.serviceUri}/corptaxportal`;
    const queryClient = useQueryClient();
    const { deleteDatabase } = useIndexedDB(window.indexedDB, DB_VERSION);

    const {
        data: notificationsResponse,
        isFetching: isGetAllNotificationsFetching,
        isError: isGetAllNotificationsFetchingError,
        refetch: refetchGetAllNotifications,
        queryKey: getNotificationsQueryKey,
    } = useGetNotifications();
    const { mutateAsync: updateNotifications } = useUpdateNotification({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getNotificationsQueryKey);
            },
        },
    });
    const { populateNotificationLinks } = useNotificationLinks({ reviewAppUrl: calculationReviewUrl ?? '/' });
    populateNotificationLinks(notificationsResponse?.notifications as any);

    useUpdatedNotificationListener({
        signalrConnectionInfo: signalRConnectionInfo,
        notificationsQueryKey: getNotificationsQueryKey as string[],
    });

    const { pendingNotificationCount } = useNewNotificationListenerHelper(
        signalRConnectionInfo,
        notificationsResponse?.notifications as any
    );
    const errorStatus = useNotificationsErrorStatus(isGetAllNotificationsFetchingError);
    const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

    //get all props related to notification settings (will be null if the feature is disabled)
    const { notificationSettingsProps } = useNotificationSettingsProps(isNotificationPanelOpen);

    const toggleNotificationRead = async (notificationId: string, isRead: boolean) => {
        try {
            await updateNotifications({ data: { id: notificationId, isRead } });
        } catch (error) {
            console.error('Error updating notification read status:', error);
            errorStatus.handleModifyNotificationError();
        }
    };
    const handleOpenChange = (_: any, data: { open: boolean }) => {
        if (data.open) {
            if (!isGetAllNotificationsFetching) {
                if (pendingNotificationCount > 0 || isGetAllNotificationsFetchingError) {
                    refetchGetAllNotifications();
                }
            }

            //always reset the error message data when the popover is opened
            errorStatus.reset();
        }

        //whenever the user interacts with the bell, let's verify that the signalr connection info was fetched successfully
        if (isSignalRConnectionInformationErrored) {
            refetchSignalRConnectionInformation();
        }

        setIsNotificationPanelOpen(!!data.open);
    };

    const handleRefetchData: () => void = () => {
        refetchGetAllNotifications();
        errorStatus.reset();
    };

    const handleSignout = (signInAsDifferentUser: boolean = false) => {
        try {
            const marker = `_${PILLAR2_SUBMITTED_REPORT_PREFIX}_`;
            for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
                const key = window.sessionStorage.key(i);
                if (key?.includes(marker)) {
                    window.sessionStorage.removeItem(key);
                }
            }
        } catch (error) {
            console.error('Error clearing Pillar 2 submitted flags from session storage:', error);
        }
        try {
            clearFormZoomSettingsFromSession();
        } catch (error) {
            console.error('Error clearing form zoom settings from session storage:', error);
        }
        signout(signInAsDifferentUser);
        try {
            clearAllCompletedReports();
        } catch (error) {
            console.error('Error clearing completed reports:', error);
        }
        try {
            deleteDatabase(DB_FORM_CACHE_NAME);
        } catch (error) {
            console.error('Error deleting Form indexedDB:', error);
        }
        try {
            deleteDatabase(DB_WHITEPAPERREPORT_NAME);
        } catch (error) {
            console.error('Error deleting WhitePaper indexedDB:', error);
        }
    };

    const signout = async (signInAsDifferentUser: boolean) => {
        try {
            if (typeof window !== 'undefined') {
                await clearPillar2UkAuthOnBff(window.location.host);
            }
        } catch {
            // Continue Review sign-out even if UK Pillar 2 token clear fails.
        }
        try {
            const result = await fetchSessionClaims();
            const logoutUrl = result?.data?.find((c) => c.type === BFF_LOGOUT_URL_CLAIM_TYPE)?.value ?? '';
            if (logoutUrl.length > 1) {
                const redirectUrl = signInAsDifferentUser
                    ? getLogoutUrlWithSignInAsAnotherUser(logoutUrl, userClaims?.environmentName ?? '')
                    : getLogoutUrl(logoutUrl);
                replaceLocation(redirectUrl);
            } else {
                console.error('Bff logout url not found in session data');
                replaceLocation(LOGIN_URL);
            }
        } catch (err) {
            console.error('Error retrieving Bff logout in session data:', err);
            replaceLocation(LOGIN_URL);
        }
    };

    const handleNotificationContent = (notification: IAlertNotification) => {
        if (notification.type === NotificationType.Collaboration) {
            const collaborationNotification = notification as ICollaborationNotification;
            return <StyledHtmlViewer htmlString={collaborationNotification.data.comment} />;
        }

        if (notification.type === NotificationType.JobFinished) {
            const exportFileNotification = notification as IExportFileNotification;
            const notificationData = exportFileNotification.data.notificationData;

            if (notificationData) {
                return (
                    <ExportFileNotificationActions
                        onPrint={() => printTaxReturnPdf(notificationData.batchProcessId)}
                        onDownload={() => downloadTaxReturnPdf(notificationData.batchProcessId, notificationData.reportName)}
                    />
                );
            }
        }

        return <></>;
    };

    return (
        <CommonTopBar
            aria-label='Calculation Review'
            leftContent={{
                className: styles.titleContainer,
                content: (
                    <>
                        <CorptaxHomeLogoLink logoHref={homeUrl ?? '/'} />
                        <Text as='h1'>{t('review')}</Text>
                    </>
                ),
            }}
            rightContent={{
                content: (
                    <>
                        {!isFeatureFlagLoading && isEnterpriseSelectorEnabled && <EnterpriseSelectorContainer />}
                        <NotificationBell
                            isLoading={isGetAllNotificationsFetching}
                            errorStatus={errorStatus}
                            pendingNotificationCount={pendingNotificationCount}
                            notifications={notificationsResponse?.notifications as any}
                            onToggleNotificationRead={toggleNotificationRead}
                            isPopoverOpen={isNotificationPanelOpen}
                            onHandleOpenChange={handleOpenChange}
                            onHandleRefetchData={handleRefetchData}
                            onRenderNotificationCardContent={handleNotificationContent}
                            notificationSettingsProps={notificationSettingsProps}
                        />
                        <UserMenuContainer
                            userDisplayName={userClaims?.userFullName ?? ''}
                            portalUrl={portalUrl}
                            idpUrl={idpUrl}
                            onSignout={() => handleSignout(false)}
                            onSignInDifferentUser={() => handleSignout(true)}
                            userSub={userClaims?.sub ?? ''}
                        />
                    </>
                ),
            }}
        />
    );
};
